import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import { sanityClient, STEP_CONFIG_QUERY } from "../sanity";
import {
  getCachedStepConfig,
  mergeComponentBlockFields,
} from "./cached-config";
import { useWorkflowVariant } from "../growthbook";
import { useGetCategoryName } from "../../hooks/use-get-category-name";
import type { StepConfig } from "../../types/workflow-schema";

/**
 * Fetches the full step config from Sanity for a given stepId.
 * Contains title, description, fields, and validation rules.
 *
 * - Fetched per page on demand
 * - Cached for 5 minutes (allows live edits to propagate)
 * - Falls back to build-time cached config if Sanity is unreachable
 * - Variant-scoped: step is looked up within the active workflow variant
 * - Category-scoped: searches only the active category's steps so each category
 *   can have different content for the same stepId
 */
export const useStepConfig = (stepId: string) => {
  const variantId = useWorkflowVariant();
  const category = useGetCategoryName();
  const categorySlug = category?.slug ?? null;

  const { data, isLoading, isPlaceholderData, error } =
    useQuery<StepConfig | null>({
      queryKey: ["step-config", variantId, categorySlug, stepId],
      queryFn: async () => {
        let result = await sanityClient.fetch<StepConfig | null>(
          STEP_CONFIG_QUERY,
          { stepId, variantId, categorySlug },
        );

        // Unknown / misconfigured variant, or a valid variant that is missing
        // this step. Coalesce to the live "default" workflow's step before
        // resorting to the build-time cached snapshot.
        if (!result && variantId !== "default") {
          result = await sanityClient.fetch<StepConfig | null>(
            STEP_CONFIG_QUERY,
            { stepId, variantId: "default", categorySlug },
          );
        }

        if (!result) {
          return getCachedStepConfig(stepId, categorySlug);
        }

        // Sanity returns getStartedContent for the get-started step.
        // Merge it into stepConfig.textBlocks so all app code reads from one place.
        const r = result as any;
        if (r.getStartedContent && Object.keys(r.getStartedContent).length) {
          r.textBlocks = { ...(r.textBlocks ?? {}), ...r.getStartedContent };
        }
        delete r.getStartedContent;

        return mergeComponentBlockFields(result);
      },
      staleTime: 5 * 60 * 1000, // 5 minutes — allows live edits to propagate
      retry: 1,
      enabled: !!stepId,
      placeholderData: () => getCachedStepConfig(stepId, categorySlug),
    });

  // TEMP DEBUG: confirm which variant + Sanity content is actually rendering.
  // Remove once the variant-flip investigation is closed out.
  useEffect(() => {
    if (!data) return;
    console.log(
      "[useStepConfig]",
      stepId,
      "| variant:",
      variantId,
      "| source:",
      isPlaceholderData ? "placeholder (build-time cache)" : "sanity",
      "| contentBlocks:",
      data.contentBlocks?.map((b: any) => ({
        key: b.key,
        type: b.blockType,
        ...(b.options ? { options: b.options } : {}),
      })),
      "| fields:",
      data.fields?.map((f: any) => ({ name: f.name, options: f.options })),
    );
  }, [stepId, variantId, data, isPlaceholderData]);

  return {
    stepConfig: data ?? getCachedStepConfig(stepId, categorySlug),
    isLoading,
    // True once the real Sanity response (or its resolved fallback) has
    // landed — false while only the placeholderData (build-time cache) is
    // showing. `isLoading` alone doesn't work for this: placeholderData
    // makes the query report status "success" immediately.
    isStepConfigReady: !isPlaceholderData && data != null,
    error,
  };
};
